import {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
	type ReactNode,
} from 'react';
import type {
	V2AnswerPageResponse,
	V2Answers,
	V2ElementValue,
	V2ErrorBody,
	V2InvestigationDetails,
	V2Page,
	V2ValidationErrorEntry,
} from '../types/V2Types';
import { V2RequestError, useQueryAnswerPage } from '../hooks/useQueryAnswerPage';

/**
 * HRR Phase 1 client-side state machine.
 *
 * Lifecycle: `loading` → `welcome` → `page` (loop, with optional `submitting`)
 *           → `done` | `error`
 *
 * Kept deliberately separate from the legacy `InvestigationContext` so the
 * legacy flow is untouched (see brief). All page navigation goes through this
 * context; renderers stay stateless and just call `setAnswer` / `submitPage`.
 */

export type V2Phase =
	| 'idle'
	| 'loading'
	| 'welcome'
	| 'page'
	| 'submitting'
	| 'done'
	| 'error'
	// Terminal: the compliance case is CLOSED (BE 409 `reason: 'caseTerminal'`).
	// Renders the `hrr-session-closed` screen; the FE must never re-submit (RND-4283).
	| 'closed'
	// Terminal: the link is expired/revoked (boot 401/403 → ExpiredLinkRefineError).
	// Renders the `hrr-expired` screen (RND-4283).
	| 'expired';

/**
 * Hard ceiling on a single answerPage submit (RND-4283). A never-resolving /
 * hung answerPage previously pinned phase `submitting` forever (Next/Back
 * disabled, no recovery surface). When this elapses we abort the in-flight
 * request and surface an inline Retry banner.
 */
export const SUBMIT_TIMEOUT_MS = 20_000;

export interface V2CompletionMessage {
	thankYouMessage?: string;
	nextStepInstructions?: string;
}

export interface V2PageContextValue {
	phase: V2Phase;
	shortCode: string | null;
	details: V2InvestigationDetails | null;
	currentPage: V2Page | null;
	answers: V2Answers;
	completion: V2CompletionMessage | null;
	/**
	 * FATAL bootstrap-time error. Set ONLY when the very first
	 * `branchinvestigationdetails` fetch fails (missing sc, BE unreachable,
	 * malformed first response). Drives `phase: 'error'` → full-screen layout.
	 *
	 * Any error that happens AFTER the user is on a real page (submitPage,
	 * goBack, retry, in-page callbacks) goes to `pageError` instead — those
	 * stay inline and never unmount the form.
	 */
	error: V2ErrorBody | null;
	/**
	 * Recoverable in-page error from submitPage / goBack / retry. The user
	 * stays on the current page (`phase: 'page'`) and PageRenderer shows an
	 * inline banner with a Retry affordance.
	 *
	 * Cleared when:
	 *  - `setAnswer` is called (the user is editing → assume they're recovering),
	 *  - a subsequent `submitPage` / `goBack` lands successfully,
	 *  - `retrySubmitPage` is called explicitly.
	 */
	pageError: V2ErrorBody | null;
	/**
	 * BE-returned page-validation errors from the most recent `answerPage` call.
	 * Populated only when the BE returns `PageValidationRefineError` (HTTP 400,
	 * refineErrorCode 2005). The user stays on the same page in `phase: 'page'`
	 * so PageRenderer can render these inline under the offending elements (or
	 * as a banner for page-scope errors).
	 *
	 * Cleared when:
	 *  - `submitPage` is called again (we re-request validation),
	 *  - the user edits any element bound to one of these errors,
	 *  - navigation moves to a different page.
	 */
	serverValidationErrors: V2ValidationErrorEntry[];

	/** True when the BE has told us the very first page has `allowBack: false`. */
	canGoBack: boolean;

	bootstrap: (shortCode: string) => Promise<void>;
	begin: () => Promise<void>;
	setAnswer: (elementId: string, value: V2ElementValue) => void;
	submitPage: () => Promise<V2AnswerPageResponse | null>;
	goBack: () => Promise<void>;
	/**
	 * Re-attempt the last failed in-page action. Clears `pageError`, re-fetches
	 * investigation details so a fresh `pageVersion` is used, then re-runs
	 * `submitPage`. Used by the inline Retry button when auto-recovery fails.
	 */
	retrySubmitPage: () => Promise<void>;
	reset: () => void;
}

// Exported (matching DataContext/InvestigationContext/LocalizationContext) so component tests can
// supply a fixture value via `V2PageContext.Provider` directly — the same boundary-control pattern
// terminalScreensCtHarness.tsx uses for LocalizationContext.
export const V2PageContext = createContext<V2PageContextValue | null>(null);

export function useV2Page(): V2PageContextValue {
	const ctx = useContext(V2PageContext);
	if (!ctx) {
		throw new Error('useV2Page must be used inside <V2PageProvider>');
	}
	return ctx;
}

interface V2PageProviderProps {
	children: ReactNode;
}

export function V2PageProvider({ children }: V2PageProviderProps) {
	const api = useQueryAnswerPage();

	const [phase, setPhase] = useState<V2Phase>('idle');
	const [shortCode, setShortCode] = useState<string | null>(null);
	const [details, setDetails] = useState<V2InvestigationDetails | null>(null);
	const [currentPage, setCurrentPage] = useState<V2Page | null>(null);
	const [answers, setAnswers] = useState<V2Answers>({});
	const [completion, setCompletion] = useState<V2CompletionMessage | null>(null);
	const [error, setError] = useState<V2ErrorBody | null>(null);
	const [pageError, setPageError] = useState<V2ErrorBody | null>(null);
	const [serverValidationErrors, setServerValidationErrors] = useState<V2ValidationErrorEntry[]>([]);
	const historyRef = useRef<string[]>([]);
	const bootstrappedRef = useRef(false);
	// Set when the BE signalled completion but the follow-up
	// `branchCompleteInvestigation` POST failed — `retrySubmitPage` reads this to
	// re-run ONLY the completion call (never re-submitting the already-answered
	// final page). See `submitOnce` done-branch (RND-4277).
	const pendingCompletionRef = useRef(false);
	// Refs that the auto-recover path needs to read without re-creating callbacks.
	const currentPageRef = useRef<V2Page | null>(null);
	const answersRef = useRef<V2Answers>({});
	const shortCodeRef = useRef<string | null>(null);
	useEffect(() => { currentPageRef.current = currentPage; }, [currentPage]);
	useEffect(() => { answersRef.current = answers; }, [answers]);
	useEffect(() => { shortCodeRef.current = shortCode; }, [shortCode]);

	/**
	 * Convert any thrown value into our wire-shape `V2ErrorBody`.
	 * Returns `null` if the error is an in-flight AbortError (caller should
	 * silently ignore — a newer request has superseded this one).
	 */
	const toErrorBody = useCallback((err: unknown): V2ErrorBody | null => {
		if (err instanceof DOMException && err.name === 'AbortError') return null;
		if (err instanceof V2RequestError) return err.body;
		const message = err instanceof Error ? err.message : String(err);
		return { errorCode: 'UnknownError', message };
	}, []);

	/**
	 * FATAL — used only by `bootstrap`. Sets `phase: 'error'` which drives the
	 * full-screen error layout. Reserved for pre-page failures (missing sc,
	 * BE unreachable at first fetch, malformed first response — before the
	 * user has ever seen a real questionnaire page).
	 */
	const handleBootstrapError = useCallback((err: unknown) => {
		const body = toErrorBody(err);
		if (!body) return; // aborted, ignore
		setError(body);
		// A 401/403 boot means the short-lived link is expired/revoked → the
		// dedicated `hrr-expired` screen instead of the generic fatal layout
		// (RND-4283). Every other bootstrap-class failure stays full-screen fatal.
		setPhase(body.errorCode === 'ExpiredLinkRefineError' ? 'expired' : 'error');
	}, [toErrorBody]);

	/**
	 * RECOVERABLE — used by `submitPage` / `goBack` / `retrySubmitPage`. Keeps
	 * `phase: 'page'` so the questionnaire stays mounted and PageRenderer
	 * surfaces an inline banner + Retry affordance. NEVER transitions to
	 * `phase: 'error'`.
	 */
	const handlePageError = useCallback((err: unknown) => {
		const body = toErrorBody(err);
		if (!body) return; // aborted, ignore
		setPageError(body);
		setPhase('page');
	}, [toErrorBody]);

	const bootstrap = useCallback(
		async (sc: string) => {
			// Guard: prevent re-entrant bootstrap calls.
			// Without this, unstable `api` refs cause the useEffect in
			// V2InvestigationFlow to fire on every render → parallel-request
			// storm → Cosmos 412 ETag conflicts.
			if (bootstrappedRef.current) return;
			bootstrappedRef.current = true;
			setPhase('loading');
			setError(null);
			setPageError(null);
			setShortCode(sc);
			historyRef.current = [];
			try {
				const investigation = await api.fetchInvestigationDetails(sc);
				setDetails(investigation);
				if (investigation.done) {
					setCompletion({
						thankYouMessage: investigation.thankYouMessage,
						nextStepInstructions: investigation.nextStepInstructions,
					});
					setPhase('done');
					return;
				}
				setCurrentPage(investigation.currentPage ?? null);
				setPhase('welcome');
			} catch (err) {
				// Bootstrap failure is the ONLY path that surfaces full-screen.
				handleBootstrapError(err);
			}
		},
		[api, handleBootstrapError],
	);

	const begin = useCallback(async () => {
		if (!details) return;
		// The BE pre-includes `currentPage` on every `branchinvestigationdetails`
		// fetch, and `goBack` to the welcome screen keeps `currentPage` set —
		// so we can always transition into 'page' phase without an extra fetch.
		// The v2 page API has no "fetch arbitrary page by id" endpoint
		// (RND-4196 §6.x); pages always come inline from currentPage / answerPage
		// / goBack responses.
		setError(null);
		setPageError(null);
		if (!currentPage) {
			// "No current page" can only happen at bootstrap-time — treat as
			// fatal so the full-screen error layout kicks in. Once the user
			// is on a real page, this branch can't be hit.
			setError({ errorCode: 'UnknownError', message: 'No current page available — please reload.' });
			setPhase('error');
			return;
		}
		setAnswers({});
		setPhase('page');
	}, [details, currentPage]);

	const setAnswer = useCallback((elementId: string, value: V2ElementValue) => {
		setAnswers((prev) => ({ ...prev, [elementId]: value }));
		// Editing any element implies the user is recovering from whatever
		// just went wrong — clear any in-page error banner so it doesn't
		// linger above a now-different form state.
		setPageError(null);
		// Clear any BE-side validation error bound to this element. The user
		// is fixing the field — the prior server complaint is now stale.
		// Page-scope errors (no elementId / multi-element) stay until the next
		// submit because we can't infer which edits resolve them.
		setServerValidationErrors((prev) => {
			if (prev.length === 0) return prev;
			const next = prev.filter((entry) => {
				if (entry.scope !== 'element') return true;
				if (entry.elementId && entry.elementId === elementId) return false;
				if (entry.elementIds && entry.elementIds.includes(elementId)) return false;
				return true;
			});
			return next.length === prev.length ? prev : next;
		});
	}, []);

	/**
	 * Core submit implementation. Performs ONE answerPage attempt and updates
	 * state on success. Throws on failure so the outer `submitPage` wrapper
	 * can decide whether to auto-recover (for StaleClientStateRefineError)
	 * or surface an in-page error banner.
	 *
	 * Takes the page + answers explicitly so the auto-recover retry can pass
	 * a refreshed `pageVersion` without racing React state.
	 */
	const submitOnce = useCallback(
		async (
			pageBeingSubmitted: V2Page,
			answersForSubmit: V2Answers,
			signal?: AbortSignal,
		): Promise<V2AnswerPageResponse> => {
			const response = await api.submitAnswer(
				pageBeingSubmitted.pageId,
				{
					pageVersion: pageBeingSubmitted.pageVersion,
					answers: answersForSubmit,
					sc: shortCodeRef.current ?? undefined,
				},
				signal,
			);
			if (response.done) {
				historyRef.current.push(pageBeingSubmitted.pageId);
				setCompletion({
					thankYouMessage: response.thankYouMessage,
					nextStepInstructions: response.nextStepInstructions,
				});
				// The v2 `answerPage` terminal response only SIGNALS completion — it
				// does not lock the case. Explicitly finalize via
				// `branchCompleteInvestigation` so the RFI is locked, the Outreach
				// Summary event is emitted, and the list flips to unread (RND-4277).
				// Only show the thank-you screen once the case is actually locked.
				// `needsComplete === false` means the BE already finalized (e.g. an
				// already-completed re-entry) — re-completing would be redundant.
				if (response.needsComplete !== false) {
					try {
						await api.completeInvestigation(shortCodeRef.current ?? undefined);
						pendingCompletionRef.current = false;
					} catch (err) {
						// Page answers are already persisted server-side; we only owe
						// the BE the completion call. Remember that and surface an
						// inline banner + Retry (retrySubmitPage re-runs ONLY the
						// completion, never re-answering the final page).
						pendingCompletionRef.current = true;
						handlePageError(err);
						return response;
					}
				}
				setPhase('done');
				return response;
			}
			// BE always returns the next page inline on a non-terminal
			// answerPage response (RND-4196 §6.2). No GET /page/:id endpoint
			// exists; if `nextPage` is missing here we treat it as a BE
			// contract violation rather than papering over with a 404 fetch.
			historyRef.current.push(pageBeingSubmitted.pageId);
			if (!response.nextPage) {
				throw new Error('answerPage response is missing nextPage');
			}
			setCurrentPage(response.nextPage);
			setAnswers({});
			setPhase('page');
			return response;
		},
		[api, handlePageError],
	);

	const submitPage = useCallback(async (): Promise<V2AnswerPageResponse | null> => {
		if (!currentPage) return null;
		setPhase('submitting');
		setPageError(null);
		setServerValidationErrors([]);
		const pageBeingSubmitted = currentPage;
		const answersBeingSubmitted = answers;
		// RND-4283: hard timeout. A hung/never-resolving answerPage used to pin
		// phase `submitting` forever. Abort the in-flight request after
		// SUBMIT_TIMEOUT_MS and surface an inline Retry banner instead.
		const controller = new AbortController();
		let timedOut = false;
		const timer = setTimeout(() => {
			timedOut = true;
			controller.abort();
		}, SUBMIT_TIMEOUT_MS);
		try {
			return await submitOnce(pageBeingSubmitted, answersBeingSubmitted, controller.signal);
		} catch (err) {
			// Timeout: the request was aborted by our own timer. The raw error is an
			// AbortError (which toErrorBody would silently ignore as a superseded
			// request), so synthesise an explicit, recoverable inline error instead.
			if (timedOut) {
				setPageError({
					errorCode: 'UnknownError',
					message: 'This is taking longer than expected. Please tap Retry to try again.',
				});
				setPhase('page');
				return null;
			}

			// PageValidationRefineError is a recoverable, user-fixable error.
			// Keep the user on the same page, hand the validationErrors to
			// PageRenderer for inline display.
			if (
				err instanceof V2RequestError &&
				err.errorCode === 'PageValidationRefineError' &&
				Array.isArray(err.body.validationErrors)
			) {
				setServerValidationErrors(err.body.validationErrors);
				setPhase('page');
				return null;
			}

			// RND-4283: a 409 carrying `body.reason === 'caseTerminal'` means the
			// compliance case is CLOSED. Route to the terminal session-closed screen
			// and NEVER re-submit. MUST be checked with an exact equality BEFORE the
			// stale auto-recover branch so tokenStale / reason-less 409s (ERR-08 /
			// IDEM-01) keep auto-recovering unchanged.
			if (err instanceof V2RequestError && err.body.reason === 'caseTerminal') {
				setError(err.body);
				setPhase('closed');
				return null;
			}

			// Auto-recover for StaleClientStateRefineError (RND-4224 Part 2):
			// the BE rotates `pageVersion` on every fetch and our client copy
			// can drift (parallel v1+v2 fetches, slow first paint, hot reload).
			// Refresh investigation details to grab the current pageVersion,
			// then re-attempt the same submit ONCE with the refreshed page.
			// Only auto-retry if the BE returns the same pageId — if the BE
			// has advanced past us we'd be re-submitting against the wrong page.
			if (err instanceof V2RequestError && err.errorCode === 'StaleClientStateRefineError') {
				try {
					const sc = shortCodeRef.current;
					if (!sc) throw new Error('Cannot auto-recover without shortCode', { cause: err });
					const fresh = await api.fetchInvestigationDetails(sc);
					const freshPage = fresh.currentPage;
					if (freshPage && freshPage.pageId === pageBeingSubmitted.pageId) {
						// Same page, refreshed pageVersion → re-submit transparently
						// (still under the same timeout window).
						setCurrentPage(freshPage);
						// Use the answers the user already entered, but route via
						// the refreshed page so the new pageVersion is sent.
						return await submitOnce(freshPage, answersBeingSubmitted, controller.signal);
					}
					// BE has moved past this page (e.g. another tab advanced) —
					// surface inline banner; user can hit Retry to pick up state.
					handlePageError(err);
					return null;
				} catch (retryErr) {
					// Auto-recover itself failed → fall through to inline banner.
					handlePageError(retryErr);
					return null;
				}
			}

			// Every other error class (network, NotFound, ShortCodeNotFound,
			// BackNotPermitted on submit, unexpected) stays inline per
			// RND-4224 Part 2: no in-page error ever full-screens the user.
			handlePageError(err);
			return null;
		} finally {
			clearTimeout(timer);
		}
	}, [submitOnce, currentPage, answers, api, handlePageError]);

	const retrySubmitPage = useCallback(async (): Promise<void> => {
		// Explicit user-driven retry. Clear the banner, refresh investigation
		// details so any stale token is recovered, then re-attempt the submit
		// against whatever page the BE now considers current (if it matches).
		setPageError(null);
		// If the only thing outstanding is the completion call (the final page was
		// already answered server-side), re-run ONLY that — re-submitting the
		// answered page would double-answer it (RND-4277).
		if (pendingCompletionRef.current) {
			setPhase('submitting');
			try {
				await api.completeInvestigation(shortCodeRef.current ?? undefined);
				pendingCompletionRef.current = false;
				setPhase('done');
			} catch (err) {
				handlePageError(err);
			}
			return;
		}
		const sc = shortCodeRef.current;
		const pageAtRetry = currentPageRef.current;
		if (!sc || !pageAtRetry) {
			// Can't retry without context — leave the user where they are.
			return;
		}
		setPhase('submitting');
		try {
			const fresh = await api.fetchInvestigationDetails(sc);
			const freshPage = fresh.currentPage;
			if (freshPage) {
				setCurrentPage(freshPage);
				if (freshPage.pageId === pageAtRetry.pageId) {
					// Same page → re-submit with user's current answers.
					await submitOnce(freshPage, answersRef.current);
					return;
				}
				// BE has moved on — show the user the page the BE thinks they're
				// on; they can fill it in fresh. Clear stale answers.
				setAnswers({});
				setPhase('page');
				return;
			}
			// No current page on refresh → treat as fatal (bootstrap-class).
			handleBootstrapError(new Error('No current page after retry'));
		} catch (err) {
			// Retry failed — keep the inline banner so the user can try again.
			handlePageError(err);
		}
	}, [api, submitOnce, handlePageError, handleBootstrapError]);

	const goBack = useCallback(async () => {
		if (!currentPage) return;
		setPageError(null);
		setServerValidationErrors([]);
		// Set phase='submitting' to drive Back/Next button `disabled` state
		// (PageRenderer derives `submitting` from this) so the customer can't
		// fire concurrent goBack requests while one is in flight.
		setPhase('submitting');
		// Always ask the BE — it is the source of truth for whether Back is
		// permitted and where the previous page lives. We previously
		// short-circuited to welcome when `historyRef.current.length === 0`,
		// but that ref isn't a reliable proxy for "first page" (it can be
		// empty on a fresh re-mount even when the BE has prior page events).
		// The BE returns BackNotPermittedRefineError when there genuinely is
		// no prior page — only then do we fall back to the welcome screen.
		try {
			const result = await api.goBack(currentPage.pageId, undefined, shortCode ?? undefined);
			// `allowed: true` is the only success shape; failures throw.
			if (!result.allowed) {
				setPhase('page');
				return;
			}
			// BE always returns the previous page inline (RND-4196 §6.3). No
			// GET /page/:id endpoint exists; missing previousPage is a BE
			// contract violation, not something to paper over with a 404 fetch.
			const previousPage = result.previousPage;
			if (!previousPage) {
				throw new Error('goBack response is missing previousPage');
			}
			// Pop history (best-effort; not load-bearing for navigation —
			// kept so other consumers reading `historyRef` stay in sync).
			if (historyRef.current.length > 0) historyRef.current.pop();
			setCurrentPage(previousPage);
			// Re-populate the page with the customer's previously-submitted
			// answers from the BE so they see what they entered before.
			setAnswers((result.initialValues ?? {}) as V2Answers);
			setPhase('page');
		} catch (err) {
			// BE says we're at the first page — transition to the welcome
			// screen. This is the ONLY non-fatal path; every other error
			// class stays inline so the user can hit Retry.
			if (err instanceof V2RequestError && err.errorCode === 'BackNotPermittedRefineError') {
				setAnswers({});
				setPhase('welcome');
				return;
			}
			// RND-4224 Part 2: in-page Back failure must NOT full-screen —
			// keep the user on the current page with an inline banner.
			handlePageError(err);
		}
	}, [api, currentPage, shortCode, handlePageError]);

	const reset = useCallback(() => {
		setPhase('idle');
		setShortCode(null);
		setDetails(null);
		setCurrentPage(null);
		setAnswers({});
		setCompletion(null);
		setError(null);
		setPageError(null);
		setServerValidationErrors([]);
		historyRef.current = [];
		pendingCompletionRef.current = false;
	}, []);

	// `canGoBack`: always true once on a page (first-page Back returns to welcome;
	// subsequent Back calls the API). BE can still block via `allowBack === false`.
	const canGoBack = useMemo(() => {
		if (!currentPage) return false;
		if (currentPage.allowBack === false) return false;
		return true;
	}, [currentPage]);

	const value = useMemo<V2PageContextValue>(
		() => ({
			phase,
			shortCode,
			details,
			currentPage,
			answers,
			completion,
			error,
			pageError,
			serverValidationErrors,
			canGoBack,
			bootstrap,
			begin,
			setAnswer,
			submitPage,
			goBack,
			retrySubmitPage,
			reset,
		}),
		[
			phase,
			shortCode,
			details,
			currentPage,
			answers,
			completion,
			error,
			pageError,
			serverValidationErrors,
			canGoBack,
			bootstrap,
			begin,
			setAnswer,
			submitPage,
			goBack,
			retrySubmitPage,
			reset,
		],
	);

	// Re-render when historyRef changes (canGoBack depends on it). We piggy-back
	// on currentPage transitions which already drive re-render — historyRef is
	// only mutated alongside currentPage updates, so this stays in sync.
	useEffect(() => {
		// no-op: the historyRef + currentPage pairing is updated atomically above.
	}, [currentPage]);

	return <V2PageContext.Provider value={value}>{children}</V2PageContext.Provider>;
}
